import { type Column, type RowData, type Table } from "@tanstack/react-table";
import { Tooltip } from "./Tooltip";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  type DropdownMenuCheckboxItemProps,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export function ColumnVisibilityToggle<TData extends RowData>({
  table,
  disabled,
}: {
  table: Table<TData>;
  disabled: boolean;
}) {
  const hideableColumns = table.getAllColumns().filter((column) => column.getCanHide());

  // Find pinned columns or fallback to first column
  const pinnedColumns = hideableColumns.filter((column) => column.getIsPinned() !== false);
  const columnToKeepVisible = pinnedColumns.length > 0 ? pinnedColumns[0] : hideableColumns[0];

  const toggleColumns = () => {
    const visibleColumns = table.getVisibleFlatColumns();
    if (visibleColumns.length === 1) {
      table.resetColumnVisibility();
    } else {
      // Always keep pinned columns (or first column if none pinned) visible, hide all others
      table.setColumnVisibility({
        ...hideableColumns.reduce((acc, column) => {
          const isPinned = column.getIsPinned() !== false;
          // If there are pinned columns, keep all pinned columns visible
          // Otherwise, keep the first column visible
          const shouldKeepVisible =
            pinnedColumns.length > 0 ? isPinned : columnToKeepVisible && column.id === columnToKeepVisible.id;
          return shouldKeepVisible ? { ...acc, [column.id]: true } : { ...acc, [column.id]: false };
        }, {}),
      });
    }
  };

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="ml-auto" disabled={disabled}>
          Columns
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="center" side="left" className="max-h-[80vh] overflow-y-auto">
        <DropdownMenuCheckboxItem
          onSelect={(event) => event.preventDefault()}
          onCheckedChange={toggleColumns}
          className="font-semibold"
        >
          {table.getVisibleFlatColumns().length === 1 ? "Show All" : "Hide"}
        </DropdownMenuCheckboxItem>
        <DropdownMenuSeparator />

        {hideableColumns
          .sort((a, b) => {
            const firstHeader = getColumnHeader(a);
            const secondHeader = getColumnHeader(b);
            if (firstHeader < secondHeader) {
              return -1;
            }
            if (firstHeader > secondHeader) {
              return 1;
            }
            return 0;
          })
          .map((column) => {
            const isPinnedColumn = column.getIsPinned() !== false;
            const visibleColumns = table.getVisibleFlatColumns();
            const isOnlyVisibleColumn = visibleColumns.length === 1 && column.getIsVisible();
            const isFirstColumn = columnToKeepVisible && column.id === columnToKeepVisible.id;

            // Disable if it's the pinned column, the first column (when no pinned), or if it's the only visible column
            const isDisabled = isPinnedColumn || isOnlyVisibleColumn;

            const checkboxProps: DropdownMenuCheckboxItemProps = {
              className: "capitalize",
              checked: column.getIsVisible(),
              onSelect: (event: Event) => event.preventDefault(),
              onCheckedChange: (value: boolean) => {
                // Prevent hiding the pinned column
                if (isPinnedColumn && !value) {
                  return;
                }
                column.toggleVisibility(!!value);
              },
              disabled: isDisabled,
            };

            const tooltipLabel = isPinnedColumn
              ? "Pinned column cannot be hidden"
              : pinnedColumns.length === 0 && isFirstColumn
                ? "At least one column must remain visible"
                : isOnlyVisibleColumn
                  ? "At least one column must remain visible"
                  : undefined;

            return tooltipLabel ? (
              <Tooltip key={column.id} label={tooltipLabel} side="left">
                <DropdownMenuCheckboxItem {...checkboxProps}>{getColumnHeader(column)}</DropdownMenuCheckboxItem>
              </Tooltip>
            ) : (
              <DropdownMenuCheckboxItem key={column.id} {...checkboxProps}>
                {getColumnHeader(column)}
              </DropdownMenuCheckboxItem>
            );
          })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function getColumnHeader<TData extends RowData>(column: Column<TData>) {
  if (typeof column.columnDef.header === "string") {
    return column.columnDef.header;
  }
  if (typeof column.columnDef.header === "function") {
    return column.id;
  }
  return column.id;
}
